import { createContext, useContext, useEffect, useRef, useState, type CSSProperties, type FocusEvent, type KeyboardEvent, type ReactNode, type RefObject } from 'react'
import type { Capability } from '../../lib/api'
import { recordOrigin, type Origin } from '../../lib/origin'
import { PageTabList, type PageTab } from '../kit'
import { hashFor, isOldAddress, partAnchor, partLabel, placeFromHash, sectionOf, tabsFor, type Place, type SectionKey } from './sections'

// The vendor record's tab row, the parts of the open tab, and the one part on
// screen (release 3.3, the 7 Oct plan section 3.3, task V1).
//
// # WHY THIS EXISTS
//
// The record was a side menu of seven sections, and inside a section every
// part stacked in one long scroll: Profile alone was its details, its contacts
// and its legal and bank details, three screens tall. Now the sections are a
// row of tabs across the page (sections.ts says which and why), and a tab with
// parts shows one of them at a time, chosen in a segmented control, so every
// tab is at most a window and a half and the part a reader came for is at the
// top of it.
//
// # WHAT A TAB MUST CARRY
//
// Behind a tab nothing is seen by accident, so a tab carries its count (the
// messages, the open findings) and a mark where something in it needs somebody
// (a finding that is critical or late, an identifier missing, an offboarding
// proposed): PageTabs' rule, the kit's own.
//
// # WHAT STAYS IN VIEW
//
// Scrolled down a long part, the reader keeps the vendor's name and the tab
// row, and nothing else: they stick under the console's top line, at most
// 120 px together (the plan's acceptance). The name shows in the row only once
// the header with the full name has gone off the top, so it is never said
// twice on one screen.

// The count a tab shows: a number, or "!" for something that is a state and
// not a count, and the words a screen reader hears for it.
export type MenuCount = { n: number | string; hot?: boolean; label: string }

// The tab row for this reader: History only with audit.read, each tab with its
// count and its mark (PageTab).
export function vendorTabList(counts: Partial<Record<SectionKey, MenuCount>>, allowed: (c: Capability) => boolean): PageTab<SectionKey>[] {
  return tabsFor(allowed).map((s) => {
    const c = counts[s.key]
    const tab: PageTab<SectionKey> = { key: s.key, label: s.tab }
    if (c && typeof c.n === 'number') tab.count = c.n
    if (c && (c.hot || typeof c.n !== 'number')) tab.alert = c.label
    return tab
  })
}

// The part on screen, and the parts opened before it on this visit, for the
// parts below to read. A part nobody has opened is not drawn at all, so its
// reads (its contacts, its findings) are made only when somebody opens it. A
// part once opened stays in the page, hidden, while another is on screen: a
// form half filled in it (the profile's edit, a sign-off's comment, a new
// contact) is still there when the reader comes back (review of V1).
type Shown = { part: string | null; opened: ReadonlySet<string> }
const ShownPart = createContext<Shown>({ part: null, opened: new Set() })

// The record's page, which tells the parts inside it which one is on screen.
export function RecordPage({ part, opened = new Set(), children }: { part: string | null; opened?: ReadonlySet<string>; children: ReactNode }) {
  return (
    <ShownPart.Provider value={{ part, opened }}>
      <div className="vd-page">{children}</div>
    </ShownPart.Provider>
  )
}

// One part of a tab: shown while it is the part on screen, hidden once opened
// and left. It is named by the words on its button in the segmented control
// (sections.ts), so it carries no title of its own unless asked.
export function Part({ id, label, titled, children }: { id: string; label: string; titled: boolean; children: ReactNode }) {
  const { part, opened } = useContext(ShownPart)
  const on = part === id
  if (!on && !opened.has(id)) return null
  const name = partLabel(id) ?? label
  return (
    <section
      id={partAnchor(id)}
      className="vd-part"
      tabIndex={-1}
      hidden={!on}
      aria-label={titled ? undefined : name}
      aria-labelledby={titled ? 'vd-part-h-' + id : undefined}
    >
      {titled && (
        <h2 className="vd-part-title" id={'vd-part-h-' + id}>
          {name}
        </h2>
      )}
      {children}
    </section>
  )
}

// The parts of the open tab as a segmented control, one pressed. Nothing for a
// tab of one part.
export function PartSwitch({ place, part, onPick }: { place: Place; part: string | null; onPick: (p: Place) => void }) {
  const s = sectionOf(place.section)
  if (s.parts.length < 2) return null
  return (
    <div className="vd-parts" role="group" aria-label={'Parts of ' + s.tab}>
      {s.parts.map((p, i) => (
        <button
          key={p.key}
          type="button"
          aria-pressed={part === p.key}
          aria-controls={part === p.key ? partAnchor(p.key) : undefined}
          data-part={p.key}
          onClick={() => onPick({ section: s.key, part: i === 0 ? null : p.key })}
        >
          {p.label}
        </button>
      ))}
    </div>
  )
}

// Whether the tab row has stuck under the top line: the header above it has
// gone off the top. Watched with an observer on a mark placed just above the
// row; with no observer (a test, an old browser) the row never says it is
// stuck, which only leaves the name out of it.
export function useStuck(mark: RefObject<HTMLElement | null>, top: number): boolean {
  const [stuck, setStuck] = useState(false)
  useEffect(() => {
    const el = mark.current
    if (!el || typeof IntersectionObserver === 'undefined') return
    const io = new IntersectionObserver(([e]) => setStuck(e != null && !e.isIntersecting && e.boundingClientRect.top < top + 1), {
      rootMargin: '-' + Math.max(0, Math.round(top)) + 'px 0px 0px 0px',
    })
    io.observe(el)
    return () => io.disconnect()
  }, [mark, top])
  return stuck
}

// The height of the console's top line, which the tab row sticks under,
// whatever height it has at this width (as the programme report does).
export function useTopLine(): number {
  const [h, setH] = useState(0)
  useEffect(() => {
    const top = document.querySelector('.topline') as HTMLElement | null
    if (!top) return
    const sync = () => setH(top.offsetHeight)
    sync()
    const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(sync) : null
    ro?.observe(top)
    return () => ro?.disconnect()
  }, [])
  return h
}

// The sticky row: the vendor's name once the header has gone, and the tabs.
//
// A press on a tab is a move the reader makes, so it pushes a history entry.
// The arrow keys, Home and End select as they go (PageTabs), so walking the
// row with them would push an entry a key: the first key pushes, and the
// rest replace while focus stays in the row, so Back steps out of the walk
// in one press (review of V1).
export function VendorTabBar({
  onPick,
  ...props
}: {
  id: string
  name: string
  tabs: PageTab<SectionKey>[]
  value: SectionKey
  onPick: (key: SectionKey, how: 'push' | 'replace') => void
}) {
  const mark = useRef<HTMLDivElement>(null)
  const top = useTopLine()
  const stuck = useStuck(mark, top)
  const walk = useRef({ key: false, walking: false })
  return (
    <TabBarView
      {...props}
      mark={mark}
      top={top}
      stuck={stuck}
      onKeyDownCapture={(e) => {
        walk.current.key = WALK_KEYS.has(e.key) && !e.altKey && !e.metaKey && !e.ctrlKey
      }}
      onBlur={(e) => {
        if (!e.currentTarget.contains(e.relatedTarget as Node | null)) walk.current.walking = false
      }}
      onPick={(key) => onPick(key, walkHow(walk.current))}
    />
  )
}

const WALK_KEYS = new Set(['ArrowLeft', 'ArrowRight', 'Home', 'End'])

// How a tab move is written: a press pushes; a walk with the keys pushes its
// first step and replaces the rest.
export function walkHow(w: { key: boolean; walking: boolean }): 'push' | 'replace' {
  if (!w.key) {
    w.walking = false
    return 'push'
  }
  w.key = false
  const how = w.walking ? 'replace' : 'push'
  w.walking = true
  return how
}

// What the row draws, given whether it has stuck: a test can draw both.
export function TabBarView({
  id,
  name,
  tabs,
  value,
  onPick,
  mark,
  top,
  stuck,
  onKeyDownCapture,
  onBlur,
}: {
  id: string
  name: string
  tabs: PageTab<SectionKey>[]
  value: SectionKey
  onPick: (key: SectionKey) => void
  mark?: RefObject<HTMLDivElement | null>
  top: number
  stuck: boolean
  onKeyDownCapture?: (e: KeyboardEvent<HTMLDivElement>) => void
  onBlur?: (e: FocusEvent<HTMLDivElement>) => void
}) {
  return (
    <>
      <div ref={mark} className="vd-tabbar-mark" aria-hidden="true" />
      <div
        className={'vd-tabbar' + (stuck ? ' vd-tabbar-stuck' : '')}
        style={top ? ({ '--vd-top': top + 'px' } as CSSProperties) : undefined}
        onKeyDownCapture={onKeyDownCapture}
        onBlur={onBlur}
      >
        {/* The header's h1 has gone off the top; this copy is for the eye
            only, the tab row's own name says whose record it is. */}
        {stuck && (
          <p className="vd-tabbar-name" aria-hidden="true">
            {name}
          </p>
        )}
        <PageTabList id={id} label={'Record of ' + name} tabs={tabs} value={value} onChange={onPick} />
      </div>
    </>
  )
}

// The history entry a tab pushes (Back model B5): the visit's origin is kept,
// so the breadcrumb still says where the vendor was opened from on every tab
// (B4), and the entry gets a scroll key of its own (lib/scrollPlace.ts), so
// Back to it returns to where the reader was on that tab rather than sharing
// the key of the tab below it. A drawer's mark is not carried over: the new
// entry is not the drawer's.
export function tabEntryState(state: unknown, key: string = Math.random().toString(36).slice(2, 10)): Record<string, unknown> {
  const { k: _k, drawer: _d, drawerDoc: _dd, ...kept } = (state ?? {}) as Record<string, unknown>
  return { ...kept, k: key }
}

// Writes a move on the record into the address: a new tab pushes an entry, a
// part of the same tab replaces it. Neither raises hashchange, so the record
// stays mounted. Says which it did, or null when the address already says it.
export function writePlace(vendorId: string, was: Place, next: Place, how?: 'push' | 'replace'): 'push' | 'replace' | null {
  const to = hashFor(vendorId, next)
  if (window.location.hash === to) return null
  try {
    if (how === 'replace' || (how === undefined && was.section === next.section)) {
      history.replaceState(history.state, '', to)
      return 'replace'
    }
    history.pushState(tabEntryState(history.state), '', to)
    return 'push'
  } catch {
    // A browser that refuses it keeps the old address; the tab still opens.
    return null
  }
}

// Reads the address after Back, Forward or a link on the page to one of its
// own tabs. A link adds an entry that does not know where the visit began, so
// the visit's origin is written into it (B4). An old address is written again
// as the tab's own, keeping the entry and the query (?panel=request opens the
// request drawer). An address of this vendor's that names no tab the reader
// may open (a word nobody knows, History without audit.read) opens the
// overview, and says so in the address. Null when the address is not this
// vendor's record.
// The vendor's own screens under its address that are not tabs of the record.
export const VENDOR_SCREENS: ReadonlySet<string> = new Set(['invite'])

export function followAddress(vendorId: string, origin: Origin | null, allowed: (c: Capability) => boolean): Place | null {
  const hash = window.location.hash
  const at = placeFromHash(hash, vendorId, allowed)
  if (!at) {
    const path = hash.replace(/^#\/?/, '').split('?')[0]?.split('/') ?? []
    if (path[0] !== 'vendor' || path[1] !== vendorId) return null
    // A screen of the vendor's own that is not the record (App.tsx routes
    // #/vendor/<id>/invite to Send questionnaire) is left alone: rewriting it
    // would swallow the Send step's link.
    if (path[2] !== undefined && VENDOR_SCREENS.has(path[2])) return null
    try {
      const q = hash.indexOf('?')
      history.replaceState(history.state, '', '#/vendor/' + vendorId + (q < 0 ? '' : hash.slice(q)))
    } catch {
      // The overview opens all the same.
    }
    return { section: 'overview', part: null }
  }
  try {
    if (origin && !recordOrigin()) history.replaceState({ ...((history.state ?? {}) as object), from: origin }, '')
    if (isOldAddress(hash, vendorId, allowed)) {
      const q = hash.indexOf('?')
      history.replaceState(history.state, '', hashFor(vendorId, at) + (q < 0 ? '' : hash.slice(q)))
    }
  } catch {
    // Without history the tab still opens; the address keeps its old form.
  }
  return at
}
